import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Target, Users, UserMinus, Trophy, Clock, Play, X, ChevronDown, ChevronUp, Lock, UserPlus, Edit2, Check, UserCheck, Settings, Plus, Minus, MapPin, Link2, Copy, Zap, BarChart2, ExternalLink, Calendar, RefreshCw, Trash2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './BRMultistep.css';

const BRMultistepCard = ({ tournament, user, onUpdate, t, currentLanguage, socket }) => {
  // Map language code to locale for date formatting
  const getLocale = () => {
    const localeMap = {
      en: "en-US",
      he: "he-IL",
      ar: "ar-SA",
      fr: "fr-FR",
      de: "de-DE",
      ru: "ru-RU"
    };
    return localeMap[currentLanguage] || "en-US";
  };

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [showActiveMatchModal, setShowActiveMatchModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showBotController, setShowBotController] = useState(false);
  const [showKickLog, setShowKickLog] = useState(false);
  const [aliasMap, setAliasMap] = useState({}); // {kickedPlayerName: {lobbyPlayer: string, roundNumber: number}}
  const [showAliasSelector, setShowAliasSelector] = useState(null); // kickedPlayerName being aliased
  const [botSelectedMap, setBotSelectedMap] = useState("Isolated");
  const [kickLoopActive, setKickLoopActive] = useState(false);
  const [checkLoopActive, setCheckLoopActive] = useState(false);

  // Scan scores state
  const [showScanScoresModal, setShowScanScoresModal] = useState(false);
  const [scanScoresData, setScanScoresData] = useState(null);
  const [scanScoresLoading, setScanScoresLoading] = useState(false);

  // Round management state
  const [roundMap, setRoundMap] = useState("Isolated");
  const [roundRoomId, setRoundRoomId] = useState("");
  const [roundRoomUrl, setRoundRoomUrl] = useState("");
  const [roundResults, setRoundResults] = useState([]);

  // Team join form state
  const [teamName, setTeamName] = useState('');
  const [teammates, setTeammates] = useState(['']);

  // Edit team form state
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeammates, setEditTeammates] = useState(['']);

  // Settings form state
  const [settingsKillPoints, setSettingsKillPoints] = useState(1);
  const [settingsPlacements, setSettingsPlacements] = useState([]);

  // Teams that invited this user
  const [invitedTeams, setInvitedTeams] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const { brMultistepData } = tournament;

  // Sync kick loop state from server
  useEffect(() => {
    const serverKickLoopActive = brMultistepData?.kickLoopActive || false;
    setKickLoopActive(serverKickLoopActive);
  }, [brMultistepData?.kickLoopActive]);

  // Sync check loop state from server
  useEffect(() => {
    const serverCheckLoopActive = brMultistepData?.checkLoopActive || false;
    setCheckLoopActive(serverCheckLoopActive);
  }, [brMultistepData?.checkLoopActive]);

  // Socket listener for scan scores result
  useEffect(() => {
    console.log("useEffect scan scores - socket:", socket ? "connected" : "null", "tournament:", tournament._id);
    if (!socket) {
      console.log("Socket is null/undefined - listener NOT set up");
      return;
    }

    const handleScanScoresResult = (data) => {
      console.log("🔔🔔🔔 FRONTEND: brScanScoresResult EVENT RECEIVED", data);
      // Only handle if this is for our tournament
      if (data.tournamentId !== tournament._id) return;

      console.log("Received scan scores result:", data);
      setScanScoresLoading(false);

      if (data.success && data.results) {
        setScanScoresData(data);
        setShowScanScoresModal(true);
      } else {
        toast.error(data.error || t("scanScoresFailed", "Failed to scan scores"));
      }
    };

    console.log("Setting up brScanScoresResult listener for tournament:", tournament._id);
    socket.on("brScanScoresResult", handleScanScoresResult);

    return () => {
      socket.off("brScanScoresResult", handleScanScoresResult);
    };
  }, [socket, tournament._id, t]);

  // Socket listener for match started / loop state updates
  useEffect(() => {
    if (!socket) return;

    const handleBrMultistepUpdate = (data) => {
      // Only handle if this is for our tournament
      if (data.tournamentId !== tournament._id) return;

      console.log("🔄 brMultistepUpdate received:", data);

      // If match started, reset checkLoopActive state
      if (data.matchStarted) {
        console.log("🎮 Match started - resetting checkLoopActive");
        setCheckLoopActive(false);
      }

      // Refresh tournament data to get latest state
      if (onUpdate) {
        // Trigger a refresh - the parent component should refetch
        console.log("Requesting tournament refresh...");
      }
    };

    socket.on("brMultistepUpdate", handleBrMultistepUpdate);

    return () => {
      socket.off("brMultistepUpdate", handleBrMultistepUpdate);
    };
  }, [socket, tournament._id, onUpdate]);


  // Proper ObjectId comparison using toString()
  const creatorId = tournament.creator?._id?.toString?.() || tournament.creator?._id || tournament.creator?.toString?.() || tournament.creator;
  const odlUserId = user?._id?.toString?.() || user?._id;
  const isCreator = creatorId && odlUserId && creatorId === odlUserId;
  const isAdmin = ['main_admin', 'manager'].includes(user?.role);
  const canManage = isCreator || isAdmin;

  const participantCount = tournament.participants?.length || 0;
  const maxPlayers = tournament.maxPlayers || 0;
  const type = brMultistepData?.type || 'solo';
  const totalRounds = brMultistepData?.totalRounds || 3;
  const currentRound = brMultistepData?.currentRound || 0;
  const multistepStatus = brMultistepData?.multistepStatus || 'lobby';
  const standings = brMultistepData?.standings || [];
  const scheduledStart = brMultistepData?.scheduledStart ? new Date(brMultistepData.scheduledStart) : null;

  const getTeamSize = () => {
    switch (type) {
      case 'duo': return 2;
      case 'squad': return 4;
      default: return 1;
    }
  };

  const getTeammateCount = () => getTeamSize() - 1;

  const getTypeLabel = () => {
    switch (type) {
      case 'duo': return 'Duo';
      case 'squad': return 'Squad';
      default: return 'Solo';
    }
  };

  const formatScheduledStart = () => {
    if (!scheduledStart) return null;
    const now = new Date();
    const diff = scheduledStart - now;
    const isPast = diff < 0;

    const options = { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
    const formatted = scheduledStart.toLocaleDateString(getLocale(), options);

    if (isPast) return { text: formatted, status: "past" };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours < 24) {
      return { text: hours > 0 ? "in " + hours + "h " + minutes + "m" : "in " + minutes + "m", status: "soon" };
    }
    return { text: formatted, status: "upcoming" };
  };

  const getStatusColor = () => {
    switch (multistepStatus) {
      case 'lobby': return 'status-waiting';
      case 'in_progress': return 'status-active';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getStatusLabel = () => {
    switch (multistepStatus) {
      case 'lobby': return t('lobby', 'Lobby');
      case 'in_progress': return t('inProgress', 'In Progress') + ` (R${currentRound}/${totalRounds})`;
      case 'completed': return t('completed', 'Completed');
      case 'cancelled': return t('cancelled', 'Cancelled');
      default: return multistepStatus;
    }
  };

  // Clean player name by removing kick reason in parentheses
  const cleanKickedName = (name) => {
    if (!name) return name;
    // Remove anything in parentheses at the end, e.g., "PlayerName (unauthorized)" -> "PlayerName"
    return name.replace(/\s*\([^)]*\)\s*$/, "").trim();
  };

  // Get lobby players for alias selection
  const getLobbyPlayers = () => {
    if (type === 'solo') {
      return standings.map(s => s.playerName).filter(Boolean);
    } else {
      // For duo/squad, get all team members
      const players = [];
      standings.forEach(team => {
        if (team.teamMembers) {
          team.teamMembers.forEach(m => {
            if (m.userId && m.inGameName) players.push(m.inGameName);
          });
        }
      });
      return players;
    }
  };

  // Handle alias add
  const handleAddAlias = async (kickedPlayer, lobbyPlayer, roundNumber) => {
    if (!window.confirm(`Are you sure you want to alias "${lobbyPlayer}" as "${kickedPlayer}"?`)) {
      return;
    }

    const cleanedKicked = cleanKickedName(kickedPlayer);
    const command = type === 'duo'
      ? `br_duo_alias "${lobbyPlayer}" "${cleanedKicked}"`
      : `br_squad_alias "${lobbyPlayer}" "${cleanedKicked}"`;

    try {
      await handleBotCommand(command);
      setAliasMap(prev => ({
        ...prev,
        [kickedPlayer]: { lobbyPlayer, roundNumber }
      }));
      setShowAliasSelector(null);
      toast.success(`Alias added: ${lobbyPlayer} -> ${kickedPlayer}`);
    } catch (error) {
      toast.error("Failed to add alias");
    }
  };

  // Handle alias remove
  const handleRemoveAlias = async (kickedPlayer) => {
    const aliasInfo = aliasMap[kickedPlayer];
    if (!aliasInfo) return;

    const cleanedKicked = cleanKickedName(kickedPlayer);
    const command = type === 'duo'
      ? `br_duo_alias_remove "${aliasInfo.lobbyPlayer}" "${cleanedKicked}"`
      : `br_squad_alias_remove "${aliasInfo.lobbyPlayer}" "${cleanedKicked}"`;

    try {
      await handleBotCommand(command);
      setAliasMap(prev => {
        const newMap = { ...prev };
        delete newMap[kickedPlayer];
        return newMap;
      });
      toast.success(`Alias removed for ${kickedPlayer}`);
    } catch (error) {
      toast.error("Failed to remove alias");
    }
  };


  // Bot status label helper
  const getBotStatusLabel = (status) => {
    switch (status) {
      case 'idle': return t('botIdle', 'Idle - Ready');
      case 'creating_room': return t('botCreatingRoom', 'Creating Room...');
      case 'room_ready': return t('botRoomReady', 'Room Ready');
      case 'waiting_players': return t('botWaitingPlayers', 'Waiting for Players');
      case 'match_active': return t('botMatchActive', 'Match In Progress');
      case 'extracting_results': return t('botExtractingResults', 'Extracting Results...');
      case 'round_complete': return t('botRoundComplete', 'Round Complete');
      case 'error': return t('botError', 'Error');
      default: return status || 'Unknown';
    }
  };

  // Handle bot command
  const handleBotCommand = async (command) => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/bot-command`, { command });
      toast.success(response.data.message || t('commandSent', 'Command sent to bot'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('commandFailed', 'Failed to send command'));
    } finally {
      setLoading(false);
    }
  };

  // Send bot command silently (no loading state, no toast) - for sequential commands
  const sendBotCommandSilent = async (command) => {
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/bot-command`, { command }, { timeout: 15000 });
      console.log('Bot command sent:', command, response?.data?.message);
      return true;
    } catch (error) {
      console.error('Silent command failed:', command, {
        message: error?.message,
        response: error?.response?.data,
        code: error?.code,
        status: error?.response?.status
      });
      throw error; // Re-throw so caller can handle
    }
  };

  // Start bot round with map selection
  const handleStartBotRound = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/start-bot-round`, {
        roundNumber: currentRound,
        map: botSelectedMap,
        type: type
      });
      toast.success(response.data.message || t('roundStarting', 'Round starting...'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToStartRound', 'Failed to start round'));
    } finally {
      setLoading(false);
    }
  };

  // Continue using previous round's room
  const handleContinueRoom = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/continue-room`, {
        roundNumber: currentRound
      });
      toast.success(response.data.message || t('roomContinued', 'Continuing with previous room'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToContinue', 'Failed to continue room'));
    } finally {
      setLoading(false);
    }
  };


// Publish room to all players  const handlePublishRoom = async () => {    setLoading(true);    try {      const response = await axios.post();      toast.success(response.data.message || t("roomPublished", "Room published to all players!"));      if (onUpdate) onUpdate(response.data.tournament);    } catch (error) {      toast.error(error.response?.data?.message || t("publishFailed", "Failed to publish room"));    } finally {      setLoading(false);    }  };

  // Publish room to all players
  const handlePublishRoom = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/publish-room`);
      toast.success(response.data.message || t("roomPublished", "Room published to all players!"));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t("publishFailed", "Failed to publish room"));
    } finally {
      setLoading(false);
    }
  };

  // Switch from bot to manual control
  const handleSwitchToManual = async () => {
    if (!window.confirm(t('confirmSwitchToManual', 'Switch to manual control? You will need to manage rounds manually.'))) {
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/switch-control`, { control: 'manual' });
      toast.success(response.data.message || t('switchedToManual', 'Switched to manual control'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('switchFailed', 'Failed to switch control mode'));
    } finally {
      setLoading(false);
    }
  };

  // Switch from manual to bot control
  const handleSwitchToBot = async () => {
    if (!window.confirm(t('confirmSwitchToBot', 'Switch to bot control? The bot will manage rounds automatically.'))) {
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/switch-control`, { control: 'bot' });
      toast.success(response.data.message || t('switchedToBot', 'Switched to bot control'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('switchFailed', 'Failed to switch control mode'));
    } finally {
      setLoading(false);
    }
  };

  // Reconnect bot (reassign to new available bot)
  const handleReconnectBot = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/reconnect-bot`);
      toast.success(response.data.message || t('botReconnected', 'Bot reconnected successfully'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('reconnectFailed', 'Failed to reconnect bot'));
    } finally {
      setLoading(false);
    }
  };

    const handleClaimTeam = async (teamNumber) => {
    setLoading(true);
    try {
      const payload = {
        teamNumber,
        password: tournament.password ? prompt(t('enterPassword', 'Enter tournament password:')) : undefined
      };

      const response = await axios.post(`/api/br-multistep/${tournament._id}/claim-team`, payload);
      toast.success(response.data.message || t('joinedTeam', 'Joined team successfully!'));
      setShowClaimModal(false);
      setInvitedTeams([]);
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToJoinTeam', 'Failed to join team'));
    } finally {
      setLoading(false);
    }
  };

  const openJoinModal = () => {
    if (type === 'solo') {
      handleJoin();
    } else {
      // Initialize teammates array based on team size
      setTeammates(Array(getTeammateCount()).fill(''));
      setTeamName('');
      setShowJoinModal(true);
    }
  };

  const handleJoin = async (teamData = null) => {
    setLoading(true);
    try {
      const payload = {
        password: tournament.password ? prompt(t('enterPassword', 'Enter tournament password:')) : undefined
      };

      if (teamData) {
        payload.teamName = teamData.teamName;
        payload.teammates = teamData.teammates;
      }

      const response = await axios.post(`/api/br-multistep/${tournament._id}/join`, payload);
      toast.success(t('joinedTournament', 'Joined tournament!'));
      setShowJoinModal(false);
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToJoin', 'Failed to join'));
    } finally {
      setLoading(false);
    }
  };

  const handleTeamJoin = () => {
    // Validate teammates
    const filledTeammates = teammates.filter(tm => tm.trim() !== '');
    if (filledTeammates.length < getTeammateCount()) {
      toast.error(t('enterAllTeammates', `Please enter all ${getTeammateCount()} teammate names`));
      return;
    }

    // Check for duplicate names
    const lowercaseNames = filledTeammates.map(tm => tm.trim().toLowerCase());
    const uniqueNames = new Set(lowercaseNames);
    if (uniqueNames.size !== lowercaseNames.length) {
      toast.error(t('duplicateTeammate', 'Each teammate must be unique. Please remove duplicate names.'));
      return;
    }

    handleJoin({
      teamName: teamName.trim() || `Team ${user?.inGameName || user?.username}`,
      teammates: filledTeammates
    });
  };

  const handleLeave = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/leave`);
      toast.success(t('leftTournament', 'Left tournament'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToLeave', 'Failed to leave'));
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (team) => {
    setEditTeamName(team.teamName || `Team ${team.teamNumber}`);
    // Get all teammates except the leader (first confirmed member)
    const confirmedMembers = team.teamMembers?.filter(m => m.userId) || [];
    const pendingMembers = team.teamMembers?.filter(m => !m.userId) || [];

    // Build editable list: confirmed members (non-editable) + pending members (editable)
    const teamSize = type === 'duo' ? 1 : 3; // Teammates needed (excluding leader)
    const editableMembers = pendingMembers.map(m => m.inGameName || '');

    // Pad array to correct size
    while (editableMembers.length < teamSize) {
      editableMembers.push('');
    }
    setEditTeammates(editableMembers.slice(0, teamSize));
    setShowEditModal(true);
  };

  const handleEditTeam = async () => {
    // Check for duplicate names
    const filledTeammates = editTeammates.filter(t => t.trim() !== '');
    const lowercaseNames = filledTeammates.map(tm => tm.trim().toLowerCase());
    const uniqueNames = new Set(lowercaseNames);
    if (uniqueNames.size !== lowercaseNames.length) {
      toast.error(t('duplicateTeammate', 'Each teammate must be unique. Please remove duplicate names.'));
      return;
    }

    setLoading(true);
    try {
      // Update team name and teammates
      await axios.post(`/api/br-multistep/${tournament._id}/update-team`, {
        teamName: editTeamName.trim(),
        teammates: filledTeammates
      });

      toast.success(t('teamUpdated', 'Team updated successfully'));
      setShowEditModal(false);
      // Refresh tournament data
      const response = await axios.get(`/api/br-multistep/${tournament._id}`);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToUpdateTeam', 'Failed to update team'));
    } finally {
      setLoading(false);
    }
  };

  const openSettingsModal = () => {
    const pointsConfig = brMultistepData?.pointsConfig || {};
    setSettingsKillPoints(pointsConfig.killPoints || 1);
    setSettingsPlacements(pointsConfig.topPlacements || [
      { position: 1, points: 10 },
      { position: 2, points: 6 },
      { position: 3, points: 3 }
    ]);
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/br-multistep/${tournament._id}/update-settings`, {
        pointsConfig: {
          killPoints: settingsKillPoints,
          topPlacements: settingsPlacements.filter(p => p.position && p.points >= 0)
        }
      });

      toast.success(t('settingsSaved', 'Settings saved successfully'));
      setShowSettingsModal(false);
      // Refresh tournament data
      const response = await axios.get(`/api/br-multistep/${tournament._id}`);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToSaveSettings', 'Failed to save settings'));
    } finally {
      setLoading(false);
    }
  };

  const addPlacement = () => {
    const nextPosition = settingsPlacements.length > 0
      ? Math.max(...settingsPlacements.map(p => p.position)) + 1
      : 1;
    setSettingsPlacements([...settingsPlacements, { position: nextPosition, points: 0 }]);
  };

  const removePlacement = (index) => {
    setSettingsPlacements(settingsPlacements.filter((_, i) => i !== index));
  };

  const updatePlacement = (index, field, value) => {
    const updated = [...settingsPlacements];
    updated[index][field] = field === 'position' ? parseInt(value) || 0 : parseInt(value) || 0;
    setSettingsPlacements(updated);
  };

  const handleStart = async () => {
    if (!window.confirm(t('confirmStart', 'Start this tournament?'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/start`);
      toast.success(t('tournamentStarted', 'Tournament started!'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToStart', 'Failed to start'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('confirmCancel', 'Cancel this tournament? Prize pool will be refunded.'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/cancel`);
      toast.success(t('tournamentCancelled', 'Tournament cancelled'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToCancel', 'Failed to cancel'));
    } finally {
      setLoading(false);
    }
  };

  // Check if user is a participant (handle both populated and non-populated)
  const userIdStr = String(user?._id || '');

  const isParticipant = tournament.participants?.some(p => {
    const pId = String(p?._id || p || '');
    return pId === userIdStr;
  }) || false;

  // Get user's team (for duo/squad)
  const userTeam = standings.find(s => {
    if (type === 'solo') {
      const playerId = String(s.playerId?._id || s.playerId || '');
      return playerId === userIdStr;
    } else {
      return s.teamMembers?.some(m => {
        const memberId = String(m.userId?._id || m.userId || '');
        return memberId === userIdStr;
      });
    }
  });


  const teamCount = type === 'solo' ? participantCount : standings.length;
  const maxTeams = type === 'solo' ? maxPlayers : Math.floor(maxPlayers / getTeamSize());

  // Check if user has been invited to any teams (only for duo/squad in lobby)
  useEffect(() => {
    const checkInvites = async () => {
      if (!user || type === 'solo' || multistepStatus !== 'lobby' || isParticipant || userTeam) {
        setInvitedTeams([]);
        return;
      }

      setLoadingInvites(true);
      try {
        const response = await axios.get(`/api/br-multistep/${tournament._id}/my-teams`);
        setInvitedTeams(response.data.teams || []);
      } catch (error) {
        console.error('Error checking team invites:', error);
        setInvitedTeams([]);
      } finally {
        setLoadingInvites(false);
      }
    };

    checkInvites();
  }, [tournament._id, user?._id, type, multistepStatus, isParticipant, userTeam]);


  // Open round management modal
  const openRoundModal = () => {
    const currentRoundData = brMultistepData?.rounds?.[currentRound - 1] || {};
    setRoundMap(currentRoundData.map || "Isolated");
    setRoundRoomId(currentRoundData.roomId || "");
    setRoundRoomUrl(currentRoundData.roomUrl || "");
    const initialResults = standings.map(s => ({
      ...(type === "solo"
        ? { playerId: s.playerId?._id || s.playerId, playerName: s.playerName }
        : { teamNumber: s.teamNumber, teamName: s.teamName, teamMembers: s.teamMembers }),
      kills: 0,
      placement: 0
    }));
    setRoundResults(initialResults);
    setShowRoundModal(true);
  };

  // Start round with map and room details
  const handleStartRound = async () => {
    if (!roundRoomId.trim()) {
      toast.error(t("roomIdRequired", "Room ID is required"));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/start-round`, {
        roundNumber: currentRound,
        map: roundMap,
        roomId: roundRoomId.trim(),
        roomUrl: roundRoomUrl.trim()
      });
      toast.success(response.data.message || t("roundStarted", "Round started!"));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t("failedToStartRound", "Failed to start round"));
    } finally {
      setLoading(false);
    }
  };

  // Complete round with results
  const handleCompleteRound = async () => {
    const invalidResults = roundResults.filter(r => !r.placement || r.placement < 1);
    if (invalidResults.length > 0) {
      toast.error(t("allPlacementsRequired", "All placements are required"));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/br-multistep/${tournament._id}/complete-round`, {
        roundNumber: currentRound,
        results: roundResults
      });
      toast.success(response.data.message || t("roundCompleted", "Round completed!"));
      setShowRoundModal(false);
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t("failedToCompleteRound", "Failed to complete round"));
    } finally {
      setLoading(false);
    }
  };

  // Update result for a player/team
  const updateRoundResult = (index, field, value) => {
    const updated = [...roundResults];
    updated[index][field] = field === "kills" || field === "placement" ? parseInt(value) || 0 : value;
    setRoundResults(updated);
  };

  // Copy room URL to clipboard
  const copyRoomUrl = () => {
    if (roundRoomUrl) {
      navigator.clipboard.writeText(roundRoomUrl);
      toast.success(t("copiedToClipboard", "Copied to clipboard!"));
    }
  };

  // Get current round data
  const getCurrentRoundData = () => {
    if (currentRound > 0 && brMultistepData?.rounds) {
      return brMultistepData.rounds[currentRound - 1] || {};
    }
    return {};
  };

  const currentRoundData = getCurrentRoundData();
  const isRoundSetup = !currentRoundData.roomId;

  return (
    <div className={`br-multistep-card ${getStatusColor()}`}>
      <div className="br-multistep-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="br-multistep-card-info">
          <div className="br-multistep-card-title">
            <Target className="w-5 h-5 text-purple-400" />
            <span className="br-multistep-type-badge">Battle Royale {getTypeLabel()}</span>
            <span className="br-multistep-rounds">{totalRounds} {t('rounds', 'Rounds')}</span>
            {tournament.password && <Lock className="w-4 h-4 text-yellow-400" />}
          </div>
          <div className="br-multistep-card-meta">
            <span className="br-multistep-creator">
              {t('by', 'by')} {tournament.creator?.inGameName || tournament.creator?.username || 'Unknown'}
            </span>
            {(isParticipant || userTeam) && (
              <span className="br-multistep-joined-badge">• {t('joined', 'Joined')}</span>
            )}
          </div>
        </div>

        {scheduledStart && (
          <div className={"br-multistep-scheduled-banner " + (formatScheduledStart()?.status || "")}>
            <Calendar className="w-4 h-4" />
            <span className="scheduled-label">{t('startsAt', 'Starts at')}:</span>
            <span className="scheduled-datetime">{scheduledStart.toLocaleString(getLocale(), { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            {formatScheduledStart()?.status === 'soon' && (
              <span className="scheduled-countdown">({formatScheduledStart()?.text})</span>
            )}
          </div>
        )}

        <div className="br-multistep-card-stats">
          <div className="br-multistep-stat">
            <Users className="w-4 h-4" />
            <span>
              {type === 'solo'
                ? `${participantCount}/${maxPlayers}`
                : `${teamCount}/${maxTeams} ${t('teams', 'teams')}`
              }
            </span>
          </div>
          <div className="br-multistep-stat prize">
            <Trophy className="w-4 h-4" />
            <span>{tournament.prizePool || 0} {t("pts", "pts")}</span>
          </div>
          {false && scheduledStart && multistepStatus === "lobby" && (
            <div className={"br-multistep-stat scheduled " + (formatScheduledStart()?.status || "")}>
              <Calendar className="w-4 h-4" />
              <span>{formatScheduledStart()?.text}</span>
            </div>
          )}
          <span className={`br-multistep-status ${getStatusColor()}`}>
            {getStatusLabel()}
          </span>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      {expanded && (
        <div className="br-multistep-card-expanded">
          <div className="br-multistep-details">
            {canManage && (
              <div className="br-multistep-detail-row">
                <span>{t('control', 'Control')}:</span>
                <span>{brMultistepData?.control === 'bot' ? t('bot', 'Bot') : t('manual', 'Manual')}</span>
              </div>
            )}
            <div className="br-multistep-detail-row">
              <span>{t('pointsPerKill', 'Points/Kill')}:</span>
              <span>{brMultistepData?.pointsConfig?.killPoints || 1}</span>
            </div>
            {type !== 'solo' && (
              <div className="br-multistep-detail-row">
                <span>{t('teamSize', 'Team Size')}:</span>
                <span>{getTeamSize()} {t('players', 'players')}</span>
              </div>
            )}
            {scheduledStart && (
              <div className={"br-multistep-detail-row scheduled-start-row " + (formatScheduledStart()?.status || "")}>
                <span><Calendar className="w-4 h-4" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}} />{t('scheduledStart', 'Scheduled Start')}:</span>
                <span className="scheduled-start-time">{scheduledStart.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Points Distribution Button */}
          <div className="br-multistep-action-buttons-row">
            <button className="br-action-btn points-dist" onClick={() => setShowPointsModal(true)}>
              <BarChart2 className="w-4 h-4" />
              {t('pointsDistribution', 'Points Distribution')}
            </button>
            {canManage && multistepStatus === 'lobby' && (
              <button className="br-action-btn settings" onClick={openSettingsModal}>
                <Settings className="w-4 h-4" />
                {t('editSettings', 'Edit Settings')}
              </button>
            )}
          </div>

          {/* Teams/Participants display */}
          {type === 'solo' ? (
            // Solo: show participants
            tournament.participants?.length > 0 && (
              <div className="br-multistep-participants">
                <h4>{t('participants', 'Participants')} ({participantCount})</h4>
                <div className="br-multistep-participants-list">
                  {tournament.participants.slice(0, 10).map((p, i) => (
                    <span key={i} className="br-multistep-participant">
                      {p.odlUser?.inGameName || p.odlUser?.username || p.inGameName || p.username || 'Unknown'}
                    </span>
                  ))}
                  {tournament.participants.length > 10 && (
                    <span className="br-multistep-more">+{tournament.participants.length - 10} more</span>
                  )}
                </div>
              </div>
            )
          ) : (
            // Duo/Squad: show teams button only
            standings.length > 0 && (
              <div className="br-multistep-teams-compact">
                <button
                  className="view-teams-btn"
                  onClick={(e) => { e.stopPropagation(); setShowTeamsModal(true); }}
                >
                  <Users className="w-4 h-4" />
                  {t('viewTeams', 'View Teams')} ({teamCount}/{maxTeams})
                </button>
              </div>
            )
          )}


          {/* User's team info */}
          {userTeam && type !== 'solo' && (
            <div className="br-multistep-user-team">
              <h4>{t('yourTeam', 'Your Team')}</h4>
              <div className="user-team-info">
                <span className="user-team-name">{userTeam.teamName || `Team ${userTeam.teamNumber}`}</span>
                <div className="user-team-members">
                  {userTeam.teamMembers?.map((m, i) => {
                    const isYou = String(m.userId?._id || m.userId || '') === userIdStr;
                    const isConfirmed = !!m.userId;
                    return (
                      <span key={i} className={`${isYou ? 'you' : ''} ${isConfirmed ? 'confirmed' : 'pending'}`}>
                        {m.inGameName || m.username}
                        {isYou && ' (You)'}
                        {!isConfirmed && ' (Pending)'}
                      </span>
                    );
                  })}
                </div>
              </div>
              {/* Team action buttons */}
              {multistepStatus === 'lobby' && (
                <div style={{display: 'flex', gap: '10px', marginTop: '12px'}}>
                  <button
                    className="br-action-btn edit"
                    onClick={() => openEditModal(userTeam)}
                    disabled={loading}
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('editTeam', 'Edit Team')}
                  </button>
                  <button
                    className="br-action-btn leave"
                    onClick={handleLeave}
                    disabled={loading}
                  >
                    <X className="w-4 h-4" />
                    {t('leaveTeam', 'Leave Team')}
                  </button>
                </div>
              )}
            </div>
          )}

{/* Active Round Info - visible to players only after room is published */}
          {multistepStatus === "in_progress" && currentRoundData.roomId && (canManage || currentRoundData.roomPublished) && (
            <div className="br-multistep-active-round">
              <div className="active-round-header">
                <h4>
                  <Zap className="w-4 h-4" />
                  {t("activeRound", "Active Round")} {currentRound}/{totalRounds}
                </h4>
                <div className="active-round-badges">
                  <span className="round-status-badge active">{t("inProgress", "In Progress")}</span>
                  {canManage && !currentRoundData.roomPublished && (
                    <span className="round-status-badge unpublished">{t("notPublished", "Not Published")}</span>
                  )}
                </div>
              </div>
              <div className="active-round-details">
                <div className="round-detail-item">
                  <MapPin className="w-4 h-4" />
                  <span className="detail-label">{t("map", "Map")}:</span>
                  <span className="detail-value">{currentRoundData.map || "Isolated"}</span>
                </div>
                <div className="round-detail-item">
                  <span className="detail-label">{t("roomId", "Room ID")}:</span>
                  <span className="detail-value room-id">{currentRoundData.roomId}</span>
                  <button
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(currentRoundData.roomId);
                      toast.success(t("copiedToClipboard", "Copied to clipboard!"));
                    }}
                    title={t("copyRoomId", "Copy Room ID")}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                {currentRoundData.roomUrl && (
                  <div className="round-detail-item room-url-row">
                    <Link2 className="w-4 h-4" />
                    <span className="detail-label">{t("roomUrl", "Room URL")}:</span>
                    <a
                      href={currentRoundData.roomUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="room-url-link"
                    >
                      {t("joinRoom", "Join Room")}
                    </a>
                    <button
                      className="copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(currentRoundData.roomUrl);
                        toast.success(t("copiedToClipboard", "Copied to clipboard!"));
                      }}
                      title={t("copyUrl", "Copy URL")}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {/* Publish button - only for admins when room is not published */}
              {canManage && !currentRoundData.roomPublished && (
                <div className="active-round-actions">
                  <button
                    className="publish-btn"
                    onClick={handlePublishRoom}
                    disabled={loading}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {loading ? t("publishing", "Publishing...") : t("publishToPlayers", "Publish to Players")}
                  </button>
                </div>
              )}
            </div>
          )}


          {/* Bot Controller Panel - visible to admins when control is bot */}
          {console.log("Bot Controller check:", { canManage, control: brMultistepData?.control, multistepStatus, botStatus: brMultistepData?.botStatus, currentRound })}
          {canManage && brMultistepData?.control === "bot" && multistepStatus === "in_progress" && (
            <div className="br-multistep-bot-controller">
              <div className="bot-controller-header">
                <h4>
                  <Zap className="w-5 h-5" />
                  {t("botController", "Bot Controller")} - {t("round", "Round")} {currentRound}/{totalRounds}
                </h4>
                <span className={"bot-status-badge " + (brMultistepData?.botStatus || "idle")}>
                  {getBotStatusLabel(brMultistepData?.botStatus)}
                </span>
              </div>

              <div className="bot-controller-content">
                {/* Phase 1: Room Setup - when idle/scores_ready and no room yet for current round */}
                {(brMultistepData?.botStatus === "idle" || brMultistepData?.botStatus === "scores_ready" || !brMultistepData?.botStatus) && !currentRoundData.roomId && (
                  <>
                    <div className="bot-info-row map-select">
                      <span className="bot-info-label">{t("selectMap", "Select Map")}:</span>
                      <select
                        value={botSelectedMap}
                        onChange={(e) => setBotSelectedMap(e.target.value)}
                        className="bot-map-select"
                      >
                        <option value="Isolated">Isolated</option>
                        <option value="Blackout">Blackout</option>
                        {maxPlayers <= 40 && <option value="Alcatraz">Alcatraz</option>}
                      </select>
                    </div>
                    <div className="bot-controller-actions">
                      <button className="bot-action-btn start" onClick={() => handleStartBotRound()} disabled={loading}>
                        <Play className="w-4 h-4" />
                        {loading ? t("starting", "Starting...") : t("createRoom", "Create Room")}
                      </button>
                      {currentRound > 1 && brMultistepData?.rounds?.[currentRound - 2]?.roomId && (
                        <button
                          className="bot-action-btn continue"
                          onClick={() => handleContinueRoom()}
                          disabled={loading}
                        >
                          <RefreshCw className="w-4 h-4" />
                          {t("continueRoom", "Continue Room")}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* Phase 2: Room Creating - show progress */}
                {brMultistepData?.botStatus === "creating_room" && (
                  <div className="bot-creating-status">
                    <div className="bot-info-row">
                      <span className="bot-info-label">{t("status", "Status")}:</span>
                      <span className="bot-info-value creating">{t("creatingRoom", "Creating Room...")}</span>
                    </div>
                    {brMultistepData?.botCommand && (
                      <div className="bot-info-row">
                        <span className="bot-info-label">{t("command", "Command")}:</span>
                        <span className="bot-info-value command">{brMultistepData.botCommand}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Phase 3: Room Ready - show room info and Controller button */}
                {(brMultistepData?.botStatus === "room_ready" || currentRoundData.roomId) && brMultistepData?.botStatus !== "creating_room" && (
                  <>
                    {currentRoundData.map && (
                      <div className="bot-info-row">
                        <span className="bot-info-label">{t("map", "Map")}:</span>
                        <span className="bot-info-value">{currentRoundData.map}</span>
                      </div>
                    )}

                    {currentRoundData.roomId && (
                      <div className="bot-info-row">
                        <span className="bot-info-label">{t("roomId", "Room ID")}:</span>
                        <span className="bot-info-value room-id-value">{currentRoundData.roomId}</span>
                        <button
                          className="copy-btn-small"
                          onClick={() => {
                            navigator.clipboard.writeText(currentRoundData.roomId);
                            toast.success(t("copiedToClipboard", "Copied!"));
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {currentRoundData.roomUrl && (
                      <div className="bot-info-row">
                        <span className="bot-info-label">{t("roomUrl", "Room URL")}:</span>
                        <a href={currentRoundData.roomUrl} target="_blank" rel="noopener noreferrer" className="bot-room-link">
                          {t("openRoom", "Open Room")}
                        </a>
                        <button
                          className="copy-btn-small"
                          onClick={() => {
                            navigator.clipboard.writeText(currentRoundData.roomUrl);
                            toast.success(t("copiedToClipboard", "Copied!"));
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {/* Publish and Controller Buttons */}
                    <div className="bot-controller-actions">
                      {!currentRoundData.roomPublished && (
                        <button className="bot-action-btn publish" onClick={handlePublishRoom} disabled={loading}>
                          <ExternalLink className="w-4 h-4" />
                          {loading ? t("publishing", "Publishing...") : t("publish", "Publish")}
                        </button>
                      )}
                      <button className="bot-action-btn controller" onClick={() => setShowBotController(true)}>
                        <Settings className="w-4 h-4" />
                        {t("controller", "Controller")}
                      </button>
                    </div>
                  </>
                )}

                {/* Error message with retry/reconnect */}
                {brMultistepData?.botError && (
                  <div className="bot-error-section">
                    <div className="bot-error-message">
                      <X className="w-4 h-4" />
                      {brMultistepData.botError}
                    </div>
                    <div className="bot-error-actions">
                      {brMultistepData.botError.toLowerCase().includes("no bot") && (
                        <button
                          className="bot-action-btn retry"
                          onClick={() => handleStartBotRound()}
                          disabled={loading}
                        >
                          <Play className="w-4 h-4" />
                          {loading ? t("retrying", "Retrying...") : t("retry", "Retry")}
                        </button>
                      )}
                      {(brMultistepData.botError.toLowerCase().includes("not connected") ||
                        brMultistepData.botError.toLowerCase().includes("disconnected")) && (
                        <button
                          className="bot-action-btn retry"
                          onClick={() => handleReconnectBot()}
                          disabled={loading}
                        >
                          <Link2 className="w-4 h-4" />
                          {loading ? t("reconnecting", "Reconnecting...") : t("reconnectBot", "Reconnect Bot")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom actions */}
              <div className="bot-controller-footer">
                <button className="bot-action-btn manual" onClick={() => handleSwitchToManual()} disabled={loading}>
                  {t("switchToManual", "Switch to Manual")}
                </button>
              </div>
            </div>
          )}


          {/* Manual Control Panel - visible to admins when control is manual */}
          {canManage && brMultistepData?.control === "manual" && multistepStatus === "in_progress" && (
            <div className="br-multistep-manual-controller">
              <div className="manual-controller-header">
                <h4>
                  <Settings className="w-5 h-5" />
                  {t("manualControl", "Manual Control")} - {t("round", "Round")} {currentRound}/{totalRounds}
                </h4>
              </div>
              <div className="manual-controller-content">
                <p className="manual-info">
                  {t("manualModeInfo", "You are in manual mode. Use 'Manage Rounds' button to control rounds.")}
                </p>
              </div>
              <div className="manual-controller-footer">
                <button className="bot-action-btn bot" onClick={() => handleSwitchToBot()} disabled={loading}>
                  <Zap className="w-4 h-4" />
                  {t("switchToBot", "Switch to Bot")}
                </button>
              </div>
            </div>
          )}


          {/* Bot Controller Modal - full command panel */}
          {showBotController && ReactDOM.createPortal(
            <div className="br-multistep-modal-overlay" onClick={() => setShowBotController(false)}>
              <div className="modal-content bot-controller-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <Settings className="w-5 h-5" />
                    {t("botCommands", "Bot Commands")} - {t("round", "Round")} {currentRound}
                  </h3>
                  <button className="close-btn" onClick={() => setShowBotController(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="modal-body">
                  {/* Kick Loop Status Indicator */}
                  {kickLoopActive && (
                    <div className="kick-loop-indicator">
                      <Zap className="w-4 h-4 loop-icon" />
                      {t("kickLoopRunning", "Kick loop is running - click button again to stop")}
                    </div>
                  )}

                  {/* Player Management Commands */}
                  <div className="controller-section">
                    <h4>{t("playerManagement", "Player Management")}</h4>
                    <div className="controller-buttons">
                      <button
                        className={"controller-btn " + (kickLoopActive ? "active-loop" : "")}
                        onClick={() => {
                          // Calculate scroll count from actual participant count
                          // 0=24, 1=40, 2=56, 3=72, 4=88, 5=100
                          const actualPlayers = participantCount || 0;
                          let kickScroll = 0;
                          if (actualPlayers <= 24) kickScroll = 0;
                          else if (actualPlayers <= 40) kickScroll = 1;
                          else if (actualPlayers <= 56) kickScroll = 2;
                          else if (actualPlayers <= 72) kickScroll = 3;
                          else if (actualPlayers <= 88) kickScroll = 4;
                          else kickScroll = 5;
                          handleBotCommand("kick_loop_toggle " + kickScroll);
                          setKickLoopActive(!kickLoopActive);
                        }}
                        disabled={loading || checkLoopActive}
                      >
                        <UserMinus className="w-4 h-4" />
                        {kickLoopActive ? t("stopKicking", "Stop Kicking") : t("kickUnauthorized", "Kick Unauthorized")}
                      </button>
                      <button
                        className="controller-btn"
                        onClick={() => setShowKickLog(true)}
                      >
                        <BarChart2 className="w-4 h-4" />
                        {t("log", "Log")}
                      </button>
                      <button
                        className="controller-btn"
                        onClick={() => {
                          const numStr = window.prompt(t("enterSpectatorSlot", "Enter spectator slot to kick (1-5):"), "1");
                          if (numStr === null) return;
                          const num = parseInt(numStr, 10);
                          if (isNaN(num) || num < 1 || num > 5) {
                            toast.error(t("invalidSlot", "Please enter a number between 1 and 5"));
                            return;
                          }
                          handleBotCommand("br_kick_spectator " + num);
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <UserMinus className="w-4 h-4" />
                        {t("kickSpectator", "Kick Spectator")}
                      </button>
                      <button
                        className="controller-btn warning"
                        onClick={() => {
                          if (window.confirm(t("confirmClearAliases", "Are you sure you want to clear all aliases?"))) {
                            handleBotCommand("br_duo_aliases_clear");
                            setAliasMap({});
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("clearAliases", "Clear Aliases")}
                      </button>
                    </div>
                  </div>

                  {/* Player List Management */}
                  <div className="controller-section">
                    <h4>{t("playerListManagement", "Player List Management")}</h4>
                    <div className="controller-buttons">
                      <button
                        className="controller-btn warning"
                        onClick={() => {
                          if (window.confirm(t("confirmClearPlayers", "Are you sure you want to clear all players from the list?"))) {
                            const command = type === "solo" ? "br_players_solo_clear" : "br_players_duo_clear";
                            handleBotCommand(command);
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("clearPlayerList", "Clear Player List")}
                      </button>
                      <button
                        className="controller-btn primary"
                        onClick={async () => {
                          const isSolo = type === "solo";
                          const label = isSolo ? "players" : "teams";
                          if (!window.confirm(t("confirmAddAllPlayers", "Add all " + standings.length + " " + label + " to player list?"))) {
                            return;
                          }
                          setLoading(true);
                          try {
                            const command = isSolo ? "br_players_solo_add" : "br_players_duo_add";
                            for (let i = 0; i < standings.length; i++) {
                              const entry = standings[i];
                              let playerNames;
                              
                              if (isSolo) {
                                // Solo: use playerName or first team member
                                playerNames = entry.playerName || entry.teamMembers?.[0]?.inGameName;
                              } else {
                                // Duo: join team members with +
                                const members = entry.teamMembers?.filter(m => m.inGameName) || [];
                                if (members.length === 0) continue;
                                playerNames = members.map(m => m.inGameName).join(" + ");
                              }
                              
                              if (!playerNames) continue;
                              
                              toast("Adding " + (i + 1) + "/" + standings.length + ": " + playerNames, { icon: "👤" });
                              await sendBotCommandSilent(command + " " + playerNames);
                              await new Promise(r => setTimeout(r, 500));
                            }
                            toast.success(t("allPlayersAdded", "All players added to list!"));
                          } catch (err) {
                            toast.error(err?.response?.data?.message || "Failed to add players");
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <UserPlus className="w-4 h-4" />
                        {t("addAllPlayers", "Add All Players")}
                      </button>
                      <button
                        className="controller-btn"
                        onClick={async () => {
                          const isSolo = type === "solo";
                          let registeredCount = 0;
                          standings.forEach(entry => {
                            if (isSolo) {
                              if (entry.userId) registeredCount++;
                            } else {
                              const registered = entry.teamMembers?.filter(m => m.userId && m.inGameName) || [];
                              if (registered.length > 0) registeredCount++;
                            }
                          });
                          
                          const label = isSolo ? "registered players" : "teams with registered users";
                          if (!window.confirm("Add " + registeredCount + " " + label + " to player list?")) {
                            return;
                          }
                          setLoading(true);
                          try {
                            const command = isSolo ? "br_players_solo_add" : "br_players_duo_add";
                            let added = 0;
                            for (let i = 0; i < standings.length; i++) {
                              const entry = standings[i];
                              let playerNames;
                              
                              if (isSolo) {
                                if (!entry.userId) continue;
                                playerNames = entry.playerName || entry.teamMembers?.[0]?.inGameName;
                              } else {
                                const members = entry.teamMembers?.filter(m => m.userId && m.inGameName) || [];
                                if (members.length === 0) continue;
                                playerNames = members.map(m => m.inGameName).join(" + ");
                              }
                              
                              if (!playerNames) continue;
                              
                              added++;
                              toast("Adding " + added + "/" + registeredCount + ": " + playerNames, { icon: "✓" });
                              await sendBotCommandSilent(command + " " + playerNames);
                              await new Promise(r => setTimeout(r, 500));
                            }
                            toast.success("Registered players added to list!");
                          } catch (err) {
                            toast.error(err?.response?.data?.message || "Failed to add players");
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <UserCheck className="w-4 h-4" />
                        {t("addRegisteredOnly", "Add Registered Only")}
                      </button>
                    </div>
                  </div>

                  {/* Match Control Commands */}
                  <div className="controller-section">
                    <h4>{t("matchControl", "Match Control")}</h4>
                    <div className="controller-buttons">
                      <button
                        className="controller-btn primary"
                        onClick={() => {
                          if (window.confirm(t("confirmStartMatch", "Are you sure you want to start now?"))) {
                            handleBotCommand("start_match");
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <Play className="w-4 h-4" />
                        {t("startMatch", "Start Match")}
                      </button>
                      <button
                        className="controller-btn"
                        onClick={async () => {
                          const numStr = window.prompt(t("enterSpectatorCount", "Enter number of spectators (0-10):"), "0");
                          if (numStr === null) return; // Cancelled
                          const num = parseInt(numStr, 10);
                          if (isNaN(num) || num < 0 || num > 10) {
                            toast.error(t("invalidSpectatorCount", "Please enter a number between 0 and 10"));
                            return;
                          }
                          // Send commands with 1 second delay between each
                          setLoading(true);
                          try {
                            toast("Sending br_settings...", { icon: '📤' });
                            await sendBotCommandSilent("br_settings");
                            await new Promise(r => setTimeout(r, 1000));
                            toast(`Sending br_spectators ${num}...`, { icon: '📤' });
                            await sendBotCommandSilent(`br_spectators ${num}`);
                            await new Promise(r => setTimeout(r, 1000));
                            toast("Sending br_settings_confirm...", { icon: '📤' });
                            await sendBotCommandSilent("br_settings_confirm");
                            toast.success(`Spectators set to ${num}`);
                          } catch (err) {
                            console.error('Spectators error:', err);
                            const errorMsg = err?.response?.data?.message || err?.message || "Failed to set spectators";
                            toast.error(errorMsg);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading || kickLoopActive || checkLoopActive}
                      >
                        <Users className="w-4 h-4" />
                        {t("spectators", "Spectators")}
                      </button>
                    </div>
                  </div>

                                    {/* Results Extraction - Duo Only */}
                  {type === "duo" && (
                    <div className="controller-section">
                      <h4>{t("resultsExtraction", "Results Extraction")}</h4>
                      <div className="controller-buttons">
                        <button
                          className={"controller-btn " + (scanScoresLoading ? "loading" : "")}
                          onClick={() => {
                            setScanScoresLoading(true);
                            handleBotCommand("br_duo_scan_scores");
                          }}
                          disabled={loading || scanScoresLoading || kickLoopActive || checkLoopActive}
                        >
                          <BarChart2 className="w-4 h-4" />
                          {scanScoresLoading ? t("scanning", "Scanning...") : t("scanScores", "Scan Scores")}
                        </button>
                        <button
                          className="controller-btn"
                          onClick={() => handleBotCommand("leave_button")}
                          disabled={loading || kickLoopActive || checkLoopActive}
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t("clickLeave", "Click Leave")}
                        </button>
                      </div>
                    </div>
                  )}

{/* Auto Match Control Commands */}
                  <div className="controller-section">
                    <h4>{t("autoMatchControl", "Auto Match Control")}</h4>
                    {/* Check loop status indicator for duo/squad */}
                    {(type === "duo" || type === "squad") && checkLoopActive && (
                      <div className="loop-status-indicator">
                        <RefreshCw className="w-4 h-4 spin" />
                        {t("checkLoopRunning", "Check loop is running - click button again to stop")}
                      </div>
                    )}
                    <div className="controller-buttons">
                      {type === "solo" ? (
                        // Solo mode: One-shot auto start
                        <button
                          className="controller-btn"
                          onClick={() => {
                            if (window.confirm(t("confirmAutoStart", "Are you sure you want to start auto match flow?"))) {
                              const maxP = tournament.maxPlayers || 24;
                              let scrollCount = 0;
                              // Solo mode: based on players
                              // 0 = 24, 1 = 40, 2 = 56, 3 = 72, 4 = 88, 5 = 100
                              if (maxP <= 24) scrollCount = 0;
                              else if (maxP <= 40) scrollCount = 1;
                              else if (maxP <= 56) scrollCount = 2;
                              else if (maxP <= 72) scrollCount = 3;
                              else if (maxP <= 88) scrollCount = 4;
                              else scrollCount = 5;

                              handleBotCommand("br_start_solo " + scrollCount);
                            }
                          }}
                          disabled={loading || kickLoopActive}
                        >
                          <Zap className="w-4 h-4" />
                          {t("autoStart", "Auto Start")}
                        </button>
                      ) : (
                        // Duo/Squad mode: Toggle check loop
                        <button
                          className={"controller-btn " + (checkLoopActive ? "active-loop" : "")}
                          onClick={() => {
                            // Calculate scroll count from actual participant count
                            // 0=24, 1=40, 2=56, 3=72, 4=88, 5=100
                            const actualPlayers = participantCount || 0;
                            let checkScroll = 0;
                            if (actualPlayers <= 24) checkScroll = 0;
                            else if (actualPlayers <= 40) checkScroll = 1;
                            else if (actualPlayers <= 56) checkScroll = 2;
                            else if (actualPlayers <= 72) checkScroll = 3;
                            else if (actualPlayers <= 88) checkScroll = 4;
                            else checkScroll = 5;
                            handleBotCommand("check_loop_toggle " + checkScroll);
                            setCheckLoopActive(!checkLoopActive);
                          }}
                          disabled={loading || kickLoopActive}
                        >
                          {checkLoopActive ? <RefreshCw className="w-4 h-4 spin" /> : <Zap className="w-4 h-4" />}
                          {checkLoopActive
                            ? t("stopChecking", "Stop Checking")
                            : t("autoStart", "Auto Start")
                          }
                        </button>
                      )}
                      <button
                        className="controller-btn danger"
                        onClick={() => handleBotCommand("stop")}
                        disabled={loading}
                      >
                        <X className="w-4 h-4" />
                        {t("stop", "Stop")}
                      </button>
                      {type === "duo" && (
                        <button
                          className="controller-btn"
                          onClick={() => {
                            if (window.confirm(t("confirmWaitScores", "This will wait for match to end and then scan scores. Continue?"))) {
                              handleBotCommand("br_duo_scan_scores_full");
                            }
                          }}
                          disabled={loading || kickLoopActive || checkLoopActive}
                        >
                          <Clock className="w-4 h-4" />
                          {t("waitScores", "Wait Scores")}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Custom Command */}
                  <div className="controller-section">
                    <h4>{t("customCommand", "Custom Command")}</h4>
                    <div className="custom-command-row">
                      <input
                        type="text"
                        id="customBotCommand"
                        placeholder={t("enterCommand", "Enter command...")}
                        className="custom-command-input"
                      />
                      <button
                        className="controller-btn"
                        onClick={() => {
                          const input = document.getElementById("customBotCommand");
                          if (input && input.value.trim()) {
                            handleBotCommand(input.value.trim());
                            input.value = "";
                          }
                        }}
                        disabled={loading}
                      >
                        {t("send", "Send")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          , document.body)}

          {/* Scan Scores Confirmation Modal */}
          {showScanScoresModal && scanScoresData && ReactDOM.createPortal(
            <div className="br-multistep-modal-overlay" onClick={() => setShowScanScoresModal(false)}>
              <div className="modal-content scan-scores-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <BarChart2 className="w-5 h-5" />
                    {t("confirmScores", "Confirm Scanned Scores")} - {t("round", "Round")} {scanScoresData.roundNumber}
                  </h3>
                  <button className="close-btn" onClick={() => setShowScanScoresModal(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="modal-body">
                  <p className="scan-info">{t("reviewEditScores", "Review and edit the scanned scores below, then confirm to complete the round.")}</p>

                  <div className="scan-results-table" dir="ltr">
                    <div className="scan-results-header">
                      <span className="col-placement">#</span>
                      <span className="col-team">{t("team", "Team")}</span>
                      <span className="col-kills">{t("kills", "Kills")}</span>
                    </div>
                    <div className="scan-results-body">
                      {scanScoresData.results.map((result, idx) => (
                        <div key={idx} className="scan-results-row">
                          <div className="col-placement">
                            <input
                              type="number"
                              className="input-placement"
                              value={result.placement || idx + 1}
                              min="1"
                              onChange={(e) => {
                                const newResults = [...scanScoresData.results];
                                newResults[idx].placement = parseInt(e.target.value) || 1;
                                setScanScoresData({...scanScoresData, results: newResults});
                              }}
                            />
                          </div>
                          <div className="col-team">
                            {result.players?.join(" & ") || "Team " + (result.teamNumber || idx + 1)}
                          </div>
                          <div className="col-kills">
                            <input
                              type="number"
                              className="input-kills"
                              value={result.kills || 0}
                              min="0"
                              onChange={(e) => {
                                const newResults = [...scanScoresData.results];
                                newResults[idx].kills = parseInt(e.target.value) || 0;
                                setScanScoresData({...scanScoresData, results: newResults});
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="scan-actions">
                    <button
                      className="btn-cancel"
                      onClick={() => setShowScanScoresModal(false)}
                    >
                      {t("cancel", "Cancel")}
                    </button>
                    <button
                      className="btn-confirm"
                      onClick={async () => {
                        setLoading(true);
                        try {
                          // Submit the results to complete the round
                          const response = await axios.post(`/api/br-multistep/${tournament._id}/complete-round`, {
                            roundNumber: scanScoresData.roundNumber,
                            results: scanScoresData.results.map(r => ({
                              teamNumber: r.teamNumber,
                              placement: r.placement,
                              kills: r.kills || 0,
                              players: r.players || []
                            }))
                          });

                          toast.success(t("roundCompleted", "Round completed successfully!"));
                          setShowScanScoresModal(false);
                          setScanScoresData(null);
                          if (onUpdate) onUpdate(response.data.tournament);
                        } catch (error) {
                          toast.error(error.response?.data?.message || t("submitFailed", "Failed to submit results"));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      <Check className="w-4 h-4" />
                      {loading ? t("submitting", "Submitting...") : t("confirmAndComplete", "Confirm & Complete Round")}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Kick Log Modal */}
          {showKickLog && ReactDOM.createPortal(
            <div className="br-multistep-modal-overlay" onClick={() => setShowKickLog(false)}>
              <div className="modal-content kick-log-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <BarChart2 className="w-5 h-5" />
                    {t("kickLog", "Kick Log")}
                  </h3>
                  <button className="close-btn" onClick={() => setShowKickLog(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="modal-body kick-log-content">
                  {brMultistepData?.kickLog && brMultistepData.kickLog.length > 0 ? (
                    <>
                      {/* Group by round */}
                      {Array.from(new Set(brMultistepData.kickLog.map(l => l.roundNumber))).sort((a, b) => a - b).map(roundNum => (
                        <div key={roundNum} className="kick-log-round">
                          <h4>{t("round", "Round")} {roundNum}</h4>
                          <div className="kick-log-entries">
                            {brMultistepData.kickLog
                              .filter(l => l.roundNumber === roundNum)
                              .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                              .map((entry, idx) => (
                                <div key={idx} className="kick-log-entry">
                                  <span className="kick-time">
                                    {new Date(entry.timestamp).toLocaleTimeString()}
                                  </span>
                                  <span className="kick-player">{entry.playerName}</span>
                                  <span className="kick-iteration">#{entry.iteration}</span>

                                  {/* Alias +/- button */}
                                  {type !== 'solo' && (
                                    aliasMap[entry.playerName] ? (
                                      <button
                                        className="alias-btn alias-remove"
                                        onClick={() => handleRemoveAlias(entry.playerName)}
                                        title={`Remove alias: ${aliasMap[entry.playerName].lobbyPlayer}`}
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                    ) : (
                                      <button
                                        className="alias-btn alias-add"
                                        onClick={() => setShowAliasSelector(entry.playerName)}
                                        title="Add alias"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                    )
                                  )}

                                  {/* Alias indicator */}
                                  {aliasMap[entry.playerName] && (
                                    <span className="alias-indicator" title={`Aliased to: ${aliasMap[entry.playerName].lobbyPlayer}`}>
                                      → {aliasMap[entry.playerName].lobbyPlayer}
                                    </span>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="no-kicks">
                      {t("noKicksYet", "No players have been kicked yet.")}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Alias Selection Modal */}
          {showAliasSelector && ReactDOM.createPortal(
            <div className="br-multistep-modal-overlay" onClick={() => setShowAliasSelector(null)}>
              <div className="modal-content alias-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <UserPlus className="w-5 h-5" />
                    {t("selectPlayerForAlias", "Select Player for Alias")}
                  </h3>
                  <button className="close-btn" onClick={() => setShowAliasSelector(null)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="modal-body alias-modal-content">
                  <p className="alias-instruction">
                    {t("aliasInstruction", `Select a registered player to play as "${cleanKickedName(showAliasSelector)}":`)}
                  </p>

                  <div className="alias-players-grid">
                    {getLobbyPlayers().length > 0 ? (
                      getLobbyPlayers().map((player, idx) => (
                        <button
                          key={idx}
                          className="alias-player-btn"
                          onClick={() => handleAddAlias(showAliasSelector, player, currentRound)}
                        >
                          <UserCheck className="w-4 h-4" />
                          {player}
                        </button>
                      ))
                    ) : (
                      <p className="no-players">{t("noRegisteredPlayers", "No registered players found")}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Actions */}
          <div className="br-multistep-actions">
            {/* Lobby actions */}
            {multistepStatus === 'lobby' && (
              <>
                {/* Join My Team - shown when user has been invited */}
                {!isParticipant && !userTeam && invitedTeams.length > 0 && (
                  <button
                    className="br-action-btn claim"
                    onClick={() => invitedTeams.length === 1 ? handleClaimTeam(invitedTeams[0].teamNumber) : setShowClaimModal(true)}
                    disabled={loading}
                  >
                    <UserCheck className="w-4 h-4" />
                    {invitedTeams.length === 1
                      ? t('joinMyTeam', 'Join My Team')
                      : t('joinMyTeamSelect', `Join Team (${invitedTeams.length} invites)`)}
                  </button>
                )}
                {/* Create new team / Solo join */}
                {!isParticipant && !userTeam && (teamCount < maxTeams || type === 'solo') && (
                  <button className="br-action-btn join" onClick={openJoinModal} disabled={loading}>
                    <Play className="w-4 h-4" />
                    {type === 'solo' ? t('join', 'Join') : (invitedTeams.length > 0 ? t('createNewTeam', 'Create New Team') : t('joinWithTeam', 'Join with Team'))}
                  </button>
                )}
                {/* Leave button - shows for solo participants */}
                {type === 'solo' && isParticipant && (
                  <button className="br-action-btn leave" onClick={handleLeave} disabled={loading}>
                    <X className="w-4 h-4" />
                    {t('leave', 'Leave')}
                  </button>
                )}
                {canManage && (teamCount >= 2 || participantCount >= 2) && (
                  <button className="br-action-btn start" onClick={handleStart} disabled={loading}>
                    <Play className="w-4 h-4" />
                    {t('start', 'Start')}
                  </button>
                )}
              </>
            )}

            {/* In Progress actions */}
            {multistepStatus === 'in_progress' && canManage && (
              <button className="br-action-btn manage" onClick={openRoundModal} disabled={brMultistepData?.control === "bot"}>
                <Clock className="w-4 h-4" />
                {t('manageRounds', 'Manage Rounds')}
              </button>
            )}
{/* Leaderboard button - visible during and after tournament */}            {(multistepStatus === "in_progress" || multistepStatus === "completed") && (              <button className="br-action-btn leaderboard" onClick={() => setShowLeaderboardModal(true)}>                <BarChart2 className="w-4 h-4" />                {t("leaderboard", "Leaderboard")}              </button>            )}            {/* Active Match Details button - visible when round is active */}            {multistepStatus === "in_progress" && currentRoundData.roomId && (canManage || currentRoundData.roomPublished) && (              <button className="br-action-btn match-details" onClick={() => setShowActiveMatchModal(true)}>                <Target className="w-4 h-4" />                {t("matchDetails", "Match Details")}              </button>            )}

            {/* Cancel button - always visible for managers (lobby or in_progress) */}
            {canManage && (multistepStatus === 'lobby' || multistepStatus === 'in_progress') && (
              <button className="br-action-btn cancel" onClick={handleCancel} disabled={loading}>
                <X className="w-4 h-4" />
                {t('cancel', 'Cancel')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Team Join Modal */}
      {showJoinModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="br-multistep-join-modal" onClick={e => e.stopPropagation()}>
            <div className="join-modal-header">
              <h3>
                <UserPlus className="w-5 h-5" />
                {t('joinTournament', 'Join Tournament')} - {getTypeLabel()}
              </h3>
              <button className="close-btn" onClick={() => setShowJoinModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="join-modal-content">
              <div className="join-form-group">
                <label>{t('teamName', 'Team Name')} ({t('optional', 'Optional')})</label>
                <input
                  type="text"
                  placeholder={`Team ${user?.inGameName || user?.username}`}
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  maxLength={30}
                />
              </div>

              <div className="join-form-group">
                <label>{t('teammates', 'Teammates')} ({getTeammateCount()} {t('required', 'required')})</label>
                <p className="form-hint">
                  {t('enterTeammateNames', 'Enter the in-game names of your teammates')}
                </p>
                {teammates.map((tm, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`${t('teammate', 'Teammate')} ${index + 1} ${t('inGameName', 'in-game name')}`}
                    value={tm}
                    onChange={(e) => {
                      const newTeammates = [...teammates];
                      newTeammates[index] = e.target.value;
                      setTeammates(newTeammates);
                    }}
                  />
                ))}
              </div>

              <div className="join-modal-info">
                <p>
                  <Users className="w-4 h-4" />
                  {t('teamWillHave', 'Your team will have')} {getTeamSize()} {t('players', 'players')} ({t('youPlus', 'you +')} {getTeammateCount()})
                </p>
              </div>
            </div>

            <div className="join-modal-actions">
              <button className="cancel-btn" onClick={() => setShowJoinModal(false)}>
                {t('cancel', 'Cancel')}
              </button>
              <button
                className="confirm-btn"
                onClick={handleTeamJoin}
                disabled={loading}
              >
                {loading ? t('joining', 'Joining...') : t('joinTournament', 'Join Tournament')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View All Teams Modal */}
      {showTeamsModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowTeamsModal(false)}>
          <div className="br-multistep-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="teams-modal-header">
              <h3>
                <Users className="w-5 h-5" />
                {t('allTeams', 'All Teams')} ({teamCount}/{maxTeams})
              </h3>
              <button className="close-btn" onClick={() => setShowTeamsModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="teams-modal-content">
              {standings.length === 0 ? (
                <p className="no-teams">{t('noTeamsYet', 'No teams have joined yet')}</p>
              ) : (
                standings.map((team, i) => {
                  // For regular users, only show confirmed members (with userId)
                  const visibleMembers = canManage
                    ? team.teamMembers
                    : team.teamMembers?.filter(m => m.userId);
                  const confirmedCount = team.teamMembers?.filter(m => m.userId).length || 0;
                  const totalSlots = getTeamSize();

                  return (
                    <div key={i} className="team-card">
                      <div className="team-card-header">
                        <span className="team-number">#{team.teamNumber}</span>
                        <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                        <span className="team-slots">{confirmedCount}/{totalSlots}</span>
                      </div>
                      <div className="team-card-members">
                        {visibleMembers?.map((m, j) => (
                          <span key={j} className={`member ${m.userId ? 'confirmed' : 'pending'}`}>
                            {m.inGameName || m.username}
                            {m.userId && ' ✓'}
                            {!m.userId && canManage && ' (Pending)'}
                          </span>
                        ))}
                        {!canManage && confirmedCount < totalSlots && (
                          <span className="member pending-count">+{totalSlots - confirmedCount} pending</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Claim Team Modal - Select from multiple invites */}
      {showClaimModal && invitedTeams.length > 0 && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowClaimModal(false)}>
          <div className="br-multistep-claim-modal" onClick={e => e.stopPropagation()}>
            <div className="claim-modal-header">
              <h3>
                <UserCheck className="w-5 h-5" />
                {t('selectYourTeam', 'Select Your Team')}
              </h3>
              <button className="close-btn" onClick={() => setShowClaimModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="claim-modal-content">
              <p className="claim-modal-info">
                {t('multipleTeamsInvited', 'Multiple teams have added your name. Select which team to join:')}
              </p>
              <div className="claim-teams-list">
                {invitedTeams.map((team, i) => (
                  <div key={i} className="claim-team-card" onClick={() => handleClaimTeam(team.teamNumber)}>
                    <div className="claim-team-header">
                      <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                      <span className="team-leader">{t('leader', 'Leader')}: {team.leaderName}</span>
                    </div>
                    <div className="claim-team-members">
                      {team.teamMembers?.map((m, j) => (
                        <span
                          key={j}
                          className={`member ${!m.userId && (m.inGameName || m.username || '').toLowerCase() === (user?.inGameName || user?.username || '').toLowerCase() ? 'you-placeholder' : ''}`}
                        >
                          {m.inGameName || m.username}
                          {!m.userId && (m.inGameName || m.username || '').toLowerCase() === (user?.inGameName || user?.username || '').toLowerCase() && ' (You)'}
                          {m.userId && ' ✓'}
                        </span>
                      ))}
                    </div>
                    <button className="claim-team-btn" disabled={loading}>
                      <UserCheck className="w-4 h-4" />
                      {t('joinThisTeam', 'Join This Team')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="br-multistep-join-modal" onClick={e => e.stopPropagation()}>
            <div className="join-modal-header">
              <h3>
                <Edit2 className="w-5 h-5" />
                {t('editTeam', 'Edit Team')}
              </h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="join-modal-content">
              <div className="join-form-group">
                <label>{t('teamName', 'Team Name')}</label>
                <input
                  type="text"
                  placeholder={t('enterTeamName', 'Enter team name')}
                  value={editTeamName}
                  onChange={(e) => setEditTeamName(e.target.value)}
                  maxLength={30}
                />
              </div>

              <div className="join-form-group">
                <label>{t('teammates', 'Teammates')} ({t('pendingSlots', 'Pending Slots')})</label>
                <p className="form-hint">
                  {t('editTeammatesHint', 'Edit the in-game names of teammates who haven\'t joined yet')}
                </p>
                {editTeammates.map((tm, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`${t('teammate', 'Teammate')} ${index + 1} ${t('inGameName', 'in-game name')}`}
                    value={tm}
                    onChange={(e) => {
                      const newTeammates = [...editTeammates];
                      newTeammates[index] = e.target.value;
                      setEditTeammates(newTeammates);
                    }}
                  />
                ))}
              </div>

              <div className="join-modal-info">
                <p>
                  <Users className="w-4 h-4" />
                  {t('editTeamNote', 'Changes will update pending teammate slots. Confirmed members cannot be edited.')}
                </p>
              </div>
            </div>

            <div className="join-modal-actions">
              <button className="cancel-btn" onClick={() => setShowEditModal(false)}>
                {t('cancel', 'Cancel')}
              </button>
              <button
                className="confirm-btn"
                onClick={handleEditTeam}
                disabled={loading || !editTeamName.trim()}
              >
                {loading ? t('saving', 'Saving...') : t('saveChanges', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="br-multistep-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>
                <Settings className="w-5 h-5" />
                {t('matchSettings', 'Match Settings')}
              </h3>
              <button className="close-btn" onClick={() => setShowSettingsModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="settings-modal-content">
              {/* Kill Points */}
              <div className="settings-form-group">
                <label>{t('pointsPerKill', 'Points Per Kill')}</label>
                <div className="number-input-row">
                  <button
                    className="number-btn"
                    onClick={() => setSettingsKillPoints(Math.max(0, settingsKillPoints - 1))}
                    disabled={settingsKillPoints <= 0}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settingsKillPoints}
                    onChange={(e) => setSettingsKillPoints(Math.max(0, parseInt(e.target.value) || 0))}
                  />
                  <button
                    className="number-btn"
                    onClick={() => setSettingsKillPoints(Math.min(100, settingsKillPoints + 1))}
                    disabled={settingsKillPoints >= 100}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Placement Points */}
              <div className="settings-form-group">
                <label>{t('placementPoints', 'Placement Points')}</label>
                <p className="form-hint">{t('placementHint', 'Configure points for top placements')}</p>

                <div className="placements-list">
                  {settingsPlacements
                    .sort((a, b) => a.position - b.position)
                    .map((placement, index) => (
                      <div key={index} className="placement-row">
                        <div className="placement-position">
                          <span className="position-label">
                            {placement.position === 1 ? '🥇' : placement.position === 2 ? '🥈' : placement.position === 3 ? '🥉' : `#${placement.position}`}
                          </span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={placement.position}
                            onChange={(e) => updatePlacement(index, 'position', e.target.value)}
                            className="position-input"
                          />
                        </div>
                        <div className="placement-points-input">
                          <input
                            type="number"
                            min="0"
                            max="1000"
                            value={placement.points}
                            onChange={(e) => updatePlacement(index, 'points', e.target.value)}
                            placeholder="Points"
                          />
                          <span className="pts-label">{t("pts", "pts")}</span>
                        </div>
                        <button
                          className="remove-placement-btn"
                          onClick={() => removePlacement(index)}
                          title={t('remove', 'Remove')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>

                <button className="add-placement-btn" onClick={addPlacement}>
                  <Plus className="w-4 h-4" />
                  {t('addPlacement', 'Add Placement')}
                </button>
              </div>
            </div>

            <div className="settings-modal-actions">
              <button className="cancel-btn" onClick={() => setShowSettingsModal(false)}>
                {t('cancel', 'Cancel')}
              </button>
              <button
                className="confirm-btn"
                onClick={handleSaveSettings}
                disabled={loading || settingsPlacements.length === 0}
              >
                {loading ? t('saving', 'Saving...') : t('saveSettings', 'Save Settings')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Round Management Modal */}
      {showRoundModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowRoundModal(false)}>
          <div className="br-multistep-round-modal" onClick={e => e.stopPropagation()}>
            <div className="round-modal-header">
              <h3>
                <MapPin className="w-5 h-5" />
                {t("round", "Round")} {currentRound} / {totalRounds}
              </h3>
              <button className="close-btn" onClick={() => setShowRoundModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="round-modal-content">
              {isRoundSetup ? (
                /* Round Setup - Enter map and room details */
                <div className="round-setup">
                  <h4>{t("setupRound", "Setup Round")}</h4>

                  <div className="round-form-group">
                    <label>{t("selectMap", "Select Map")}</label>
                    <select
                      value={roundMap}
                      onChange={(e) => setRoundMap(e.target.value)}
                      className="map-select"
                    >
                      <option value="Isolated">Isolated</option>
                      <option value="Blackout">Blackout</option>
                      {maxPlayers <= 40 && <option value="Alcatraz">Alcatraz</option>}
                    </select>
                  </div>

                  <div className="round-form-group">
                    <label>{t("roomId", "Room ID")}</label>
                    <input
                      type="text"
                      placeholder={t("enterRoomId", "Enter room ID")}
                      value={roundRoomId}
                      onChange={(e) => setRoundRoomId(e.target.value)}
                      maxLength={20}
                    />
                  </div>

                  <div className="round-form-group">
                    <label>{t("roomUrl", "Room URL")} ({t("optional", "Optional")})</label>
                    <input
                      type="text"
                      placeholder={t("enterRoomUrl", "Enter room invite URL")}
                      value={roundRoomUrl}
                      onChange={(e) => setRoundRoomUrl(e.target.value)}
                    />
                  </div>

                  <button
                    className="start-round-btn"
                    onClick={handleStartRound}
                    disabled={loading || !roundRoomId.trim()}
                  >
                    <Play className="w-4 h-4" />
                    {loading ? t("starting", "Starting...") : t("startRound", "Start Round")}
                  </button>
                </div>
              ) : (
                /* Round Active - Enter results */
                <div className="round-results">
                  <div className="round-info-bar">
                    <div className="round-info-item">
                      <MapPin className="w-4 h-4" />
                      <span>{currentRoundData.map || "Isolated"}</span>
                    </div>
                    <div className="round-info-item">
                      <span>Room: {currentRoundData.roomId}</span>
                    </div>
                    {currentRoundData.roomUrl && (
                      <button className="copy-url-btn" onClick={copyRoomUrl}>
                        <Link2 className="w-4 h-4" />
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <h4>{t("enterResults", "Enter Results")}</h4>

                  <div className="results-table">
                    <div className="results-header">
                      <span className="col-name">{type === "solo" ? t("player", "Player") : t("team", "Team")}</span>
                      <span className="col-kills">{t("kills", "Kills")}</span>
                      <span className="col-placement">{t("placement", "Placement")}</span>
                    </div>
                    {roundResults.map((result, index) => (
                      <div key={index} className="results-row">
                        <div className="col-name">
                          {type === "solo" ? (
                            <span>{result.playerName}</span>
                          ) : (
                            <div className="team-result-info">
                              <span className="team-result-name">{result.teamName || "Team " + result.teamNumber}</span>
                              {result.teamMembers && result.teamMembers.length > 0 && (
                                <span className="team-result-members">
                                  {result.teamMembers.filter(m => m.userId).map(m => m.inGameName || "Unknown").join(", ")}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <input
                          type="number"
                          className="col-kills"
                          min="0"
                          value={result.kills}
                          onChange={(e) => updateRoundResult(index, "kills", e.target.value)}
                        />
                        <input
                          type="number"
                          className="col-placement"
                          min="1"
                          value={result.placement || ""}
                          onChange={(e) => updateRoundResult(index, "placement", e.target.value)}
                          placeholder="#"
                        />
                      </div>
                    ))}
                  </div>

                  <button
                    className="complete-round-btn"
                    onClick={handleCompleteRound}
                    disabled={loading}
                  >
                    <Check className="w-4 h-4" />
                    {loading ? t("completing", "Completing...") : t("completeRound", "Complete Round")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
{/* Leaderboard Modal */}      {showLeaderboardModal && (        <div className="br-multistep-modal-overlay" onClick={() => setShowLeaderboardModal(false)}>          <div className="br-multistep-leaderboard-modal" onClick={e => e.stopPropagation()}>            <div className="leaderboard-modal-header">              <h3>                <Trophy className="w-5 h-5" />                {t("leaderboard", "Leaderboard")} - {t("round", "Round")} {currentRound}/{totalRounds}              </h3>              <button className="close-btn" onClick={() => setShowLeaderboardModal(false)}>                <X className="w-5 h-5" />              </button>            </div>            <div className="leaderboard-content">              <div className="leaderboard-table">                <div className="leaderboard-header-row">                  <span className="col-rank">#</span>                  <span className="col-name">{type === "solo" ? t("player", "Player") : t("team", "Team")}</span>                  <span className="col-kills">{t("kills", "Kills")}</span>                  <span className="col-points">{t("points", "Points")}</span>                </div>                {standings                  .sort((a, b) => b.totalPoints - a.totalPoints)                  .map((entry, index) => (                    <div key={index} className={"leaderboard-row " + (index < 3 ? "top-" + (index + 1) : "")}>                      <span className="col-rank">                        {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : index + 1}                      </span>                      <div className="col-name">                        <span className="leaderboard-team-name">{type === "solo" ? entry.playerName : entry.teamName || "Team " + entry.teamNumber}</span>                        {type !== "solo" && entry.teamMembers && entry.teamMembers.length > 0 && (                          <span className="leaderboard-team-members">{entry.teamMembers.filter(m => m.userId).map(m => m.inGameName || "Unknown").join(", ")}</span>                        )}                      </div>                      <span className="col-kills">{entry.totalKills || 0}</span>                      <span className="col-points">{entry.totalPoints || 0}</span>                    </div>                  ))}              </div>            </div>          </div>        </div>      )}      {/* Active Match Details Modal */}      {showActiveMatchModal && currentRoundData.roomId && (canManage || currentRoundData.roomPublished) && (        <div className="br-multistep-modal-overlay" onClick={() => setShowActiveMatchModal(false)}>          <div className="br-multistep-match-modal" onClick={e => e.stopPropagation()}>            <div className="match-modal-header">              <h3>                <Target className="w-5 h-5" />                {t("activeMatchDetails", "Active Match Details")}              </h3>              <button className="close-btn" onClick={() => setShowActiveMatchModal(false)}>                <X className="w-5 h-5" />              </button>            </div>            <div className="match-modal-content">              <div className="match-info-section">                <div className="match-info-item">                  <span className="info-label">{t("round", "Round")}</span>                  <span className="info-value">{currentRound} / {totalRounds}</span>                </div>                <div className="match-info-item">                  <span className="info-label">{t("map", "Map")}</span>                  <span className="info-value">{currentRoundData.map || "Isolated"}</span>                </div>                <div className="match-info-item">                  <span className="info-label">{t("type", "Type")}</span>                  <span className="info-value">Battle Royale {type.charAt(0).toUpperCase() + type.slice(1)}</span>                </div>              </div>              <div className="match-room-section">                <div className="room-id-box">                  <span className="room-label">{t("roomId", "Room ID")}</span>                  <div className="room-value-row">                    <span className="room-value">{currentRoundData.roomId}</span>                    <button                      className="copy-btn"                      onClick={() => {                        navigator.clipboard.writeText(currentRoundData.roomId);                        toast.success(t("copiedToClipboard", "Copied!"));                      }}                    >                      <Copy className="w-4 h-4" />                      {t("copy", "Copy")}                    </button>                  </div>                </div>                {currentRoundData.roomUrl && (                  <div className="room-url-box">                    <span className="room-label">{t("roomUrl", "Room URL")}</span>                    <div className="room-value-row">                      <a                        href={currentRoundData.roomUrl}                        target="_blank"                        rel="noopener noreferrer"                        className="join-room-btn"                      >                        <ExternalLink className="w-4 h-4" />                        {t("joinRoom", "Join Room")}                      </a>                      <button                        className="copy-btn"                        onClick={() => {                          navigator.clipboard.writeText(currentRoundData.roomUrl);                          toast.success(t("copiedToClipboard", "Copied!"));                        }}                      >                        <Copy className="w-4 h-4" />                      </button>                    </div>                  </div>                )}              </div>              <div className="match-participants-section">                <h4>{type === "solo" ? t("participants", "Participants") : t("teams", "Teams")} ({standings.length})</h4>                <div className="participants-list">                  {standings.map((entry, idx) => (                    <div key={idx} className="participant-item">                      <span className="participant-number">{idx + 1}</span>                      <div className="participant-info">                        <span className="participant-name">                          {type === "solo" ? entry.playerName : entry.teamName || "Team " + entry.teamNumber}                        </span>                        {type !== "solo" && entry.teamMembers && entry.teamMembers.length > 0 && (                          <span className="participant-members">{entry.teamMembers.filter(m => m.userId).map(m => m.inGameName || "Unknown").join(", ")}</span>                        )}                      </div>                    </div>                  ))}                </div>              </div>            </div>          </div>        </div>      )}

      {/* Points Distribution Modal */}
      {showPointsModal && (
        <div className="br-multistep-modal-overlay" onClick={() => setShowPointsModal(false)}>
          <div className="br-multistep-points-modal" onClick={e => e.stopPropagation()}>
            <div className="points-modal-header">
              <h3>
                <BarChart2 className="w-5 h-5" />
                {t('pointsDistribution', 'Points Distribution')}
              </h3>
              <button className="close-btn" onClick={() => setShowPointsModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="points-modal-content">
              <div className="points-info-grid">
                <div className="points-info-item kill-points">
                  <span className="points-label">{t('perKill', 'Per Kill')}</span>
                  <span className="points-value">+{brMultistepData?.pointsConfig?.killPoints || 1} {t("pts", "pts")}</span>
                </div>
                {(brMultistepData?.pointsConfig?.topPlacements || [])
                  .sort((a, b) => a.position - b.position)
                  .map((placement, idx) => (
                    <div key={idx} className="points-info-item placement-points">
                      <span className="points-label">
                        {placement.position === 1 ? '🥇' : placement.position === 2 ? '🥈' : placement.position === 3 ? '🥉' : '#' + placement.position}
                        {' '}{t('place', 'Place')}
                      </span>
                      <span className="points-value">+{placement.points} {t("pts", "pts")}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BRMultistepCard;
